// src/hooks/useRecoveryProgram.ts
import { useEffect, useState } from "react";
import { doc, onSnapshot } from "firebase/firestore";
import { db } from "../firebase/config";
import type { RecoveryActivityItem, RecoveryWeekMeta } from "../types/recovery";

export interface WeekProgramData {
  meta: RecoveryWeekMeta;
  activities: RecoveryActivityItem[];
}

export interface UseRecoveryProgramReturn {
  /** Keyed by 1-based week number */
  weekProgram: Record<number, WeekProgramData>;
  loading: boolean;
  error: string | null;
}

const DEFAULT_META: RecoveryWeekMeta = {
  theme: "",
  intro: "",
};

const TOTAL_WEEKS = 6;

export function useRecoveryProgram(): UseRecoveryProgramReturn {
  const [weekProgram, setWeekProgram] = useState<Record<number, WeekProgramData>>({});
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const resolved = new Set<number>();
    const latestData: Record<number, WeekProgramData> = {};

    const unsubscribers = Array.from({ length: TOTAL_WEEKS }, (_, i) => {
      const weekNum = i + 1;

      return onSnapshot(
        doc(db, "program", `week_${weekNum}`),
        (snap) => {
          const data = snap.exists() ? snap.data() : undefined;

          latestData[weekNum] = {
            meta: {
              theme: data?.theme ?? DEFAULT_META.theme,
              intro: data?.intro ?? DEFAULT_META.intro,
            },
            activities: Array.isArray(data?.activities) ? data.activities : [],
          };

          resolved.add(weekNum);

          if (resolved.size === TOTAL_WEEKS) {
            setWeekProgram({ ...latestData });
            setLoading(false);
            setError(null);
          }
        },
        (err) => {
          console.error(`useRecoveryProgram: failed to load week ${weekNum}`, err);
          setError("Failed to load programme data");
          setLoading(false);
        }
      );
    });

    return () => unsubscribers.forEach((unsub) => unsub());
  }, []);

  return { weekProgram, loading, error };
}